/**
 * Fox (Bedrock numbers): 10 HP, 0.6 x 0.7, speed 0.3. Red in the taigas, white where
 * it snows. Sleeps through the day (curled up, until something comes close), keeps
 * about six blocks from the player, and at night hunts chickens for 2 a bite. Follows
 * and breeds with glow berries (this game has no sweet berry item); kits take a
 * parent's coat. Drops nothing, 1-3 XP.
 * Not implemented: pouncing, carrying items in its mouth, trusting kits, sneaking up.
 *
 * `view.color`: bit 0 snow fox, bit 1 sitting, bit 2 sleeping.
 */

import type { Stack } from '../../game/types';
import { Animal } from '../animals';
import { isBreedFood } from '../breeding';
import type { EntityHost } from '../host';
import type { Mob, MobCtx } from '../mob';
import { overworld2Hooks } from './mobs';
import { wolfHooks } from './wolf';

export const FOX_DAMAGE = 2;
const SNOWY = /snowy|frozen|ice|grove|jagged/;
const FLEE_DIST = 6;
const HUNT_RANGE = 10;

export const foxHooks: {
  /** Every live mob (EntityManager): what a hunting fox picks its chicken from. */
  mobs?: () => readonly Mob[];
} = {};

export class Fox extends Animal {
  protected raw = 0;
  protected cooked = 0;
  snow: boolean;
  sleeping = false;
  sitTicks = 0;
  /** Ticks since something last spooked it; it will not doze off until this runs out. */
  private alert = 0;
  private attackCd = 0;
  private prey: Mob | null = null;

  constructor(id: number, x: number, y: number, z: number, rnd: () => number) {
    super(id, 'fox', x, y, z, { width: 0.6, height: 0.7, eye: 0.4, health: 10, speed: 0.3 }, rnd);
    this.panicMod = 2;
    this.snow = SNOWY.test(overworld2Hooks.biomeAt?.(x, z) ?? '');
  }

  override inherit(a: Animal, b: Animal, rnd: () => number): void {
    const p = rnd() < 0.5 ? a : b;
    if (p instanceof Fox) this.snow = p.snow;
  }

  override onHurtBy(fromX: number, fromZ: number, rnd: () => number): void {
    this.sleeping = false; this.sitTicks = 0; this.alert = 200; this.prey = null;
    super.onHurtBy(fromX, fromZ, rnd);
  }

  /** Chase the nearest chicken; true while the hunt is on. */
  private hunt(ctx: MobCtx): boolean {
    if (this.phase(ctx, 20) || (this.prey && !this.prey.alive)) {
      this.prey = null;
      let bd = HUNT_RANGE * HUNT_RANGE;
      for (const m of foxHooks.mobs?.() ?? []) {
        if (m.type !== 'chicken' || !m.alive) continue;
        const d = (m.x - this.x) ** 2 + (m.y - this.y) ** 2 + (m.z - this.z) ** 2;
        if (d < bd) { bd = d; this.prey = m; }
      }
    }
    const t = this.prey;
    if (!t) return false;
    this.aggressive = true;
    this.faceToward(t.x, t.z); this.lookAt(t.x, t.y + t.height * 0.5, t.z);
    this.steerTo(ctx, t.x, t.y, t.z, 1.5, 3, true);
    const reach = this.width / 2 + t.width / 2 + 0.7;
    if (this.attackCd === 0 && Math.hypot(t.x - this.x, t.z - this.z) < reach && Math.abs(t.y - this.y) < 1.2) {
      this.attackCd = 20;
      this.swing();
      wolfHooks.attack?.(this, t, FOX_DAMAGE);
    }
    return true;
  }

  protected override ai(ctx: MobCtx): void {
    if (this.attackCd > 0) this.attackCd--;
    this.aggressive = false;
    const p = ctx.host.player, rnd = ctx.host.rnd;
    const d2 = (p.x - this.x) ** 2 + (p.y - this.y) ** 2 + (p.z - this.z) ** 2;
    const threat = ctx.targetable() && d2 < FLEE_DIST * FLEE_DIST && !isBreedFood('fox', ctx.host.heldItem?.() ?? 0);
    if (threat) this.alert = 100; else if (this.alert > 0) this.alert--;
    const night = ctx.host.isNight();
    const busy = this.panicTicks > 0 || !!this.mate || this.loveTicks > 0 || this.inWater;
    if (busy || this.alert > 0 || (night && this.sleeping)) { this.sleeping = false; this.sitTicks = 0; }
    if (this.sleeping) { this.ageTick(ctx); return; }
    if (this.sitTicks > 0) { this.sitTicks--; this.ageTick(ctx); this.lookAtNearPlayer(ctx); return; }
    if (!busy) {
      if (threat) {
        this.ageTick(ctx);
        if (this.navAway(ctx, p.x, p.z, 1.5, 3, true)) return;
      } else if (!night) {
        if (this.alert === 0 && this.onGround && rnd() < 1 / 60) { this.sleeping = true; return; }
      } else if (!this.baby && this.hunt(ctx)) {
        this.ageTick(ctx);
        return;
      } else if (this.onGround && rnd() < 1 / 500) {
        this.sitTicks = 100 + Math.floor(rnd() * 100);
        return;
      }
    }
    super.ai(ctx);
  }

  override syncView(host: EntityHost): void {
    super.syncView(host);
    this.view.color = (this.snow ? 1 : 0) | (this.sitTicks > 0 ? 2 : 0) | (this.sleeping ? 4 : 0);
  }

  protected override saveMore(o: Record<string, number>): void { o.fs = this.snow ? 1 : 0; }
  protected override loadMore(o: Record<string, number>): void { if (typeof o.fs === 'number') this.snow = o.fs === 1; }

  loot(): Stack[] { return []; }
}

/** Taiga chunks that rolled a herd: a third of them hold two to four foxes instead. */
export function foxHerd(roll: number, sizeRoll: number, biome: string): number {
  return roll < 0.33 && /taiga|grove/.test(biome) ? 2 + Math.floor(sizeRoll * 3) : 0;
}
